import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import type { Language, Translations } from './translations/types';
import { translations } from './translations/index';

interface LanguageContextType {
  lang: Language;
  setLang: (lang: Language) => void;
  t: Translations;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

// 브라우저 언어 감지: 한국이면 KO, 그 외는 EN
function detectLanguage(): Language {
  const browserLang = navigator.language || (navigator as any).userLanguage || 'en';
  // ko, ko-KR 등 한국어 계열이면 ko 반환
  if (browserLang.toLowerCase().startsWith('ko')) {
    return 'ko';
  }
  return 'en';
}

// 로컬 스토리지에서 언어 설정 불러오기
function getStoredLanguage(): Language | null {
  try {
    const stored = localStorage.getItem('tristepflow-lang');
    if (stored === 'ko' || stored === 'en') {
      return stored;
    }
  } catch {
    // localStorage 접근 실패 시 무시
  }
  return null;
}

// 로컬 스토리지에 언어 설정 저장
function storeLanguage(lang: Language): void {
  try {
    localStorage.setItem('tristepflow-lang', lang);
  } catch {
    // localStorage 접근 실패 시 무시
  }
}

interface LanguageProviderProps {
  children: ReactNode;
}

export function LanguageProvider({ children }: LanguageProviderProps) {
  const [lang, setLangState] = useState<Language>(() => {
    // 1순위: 저장된 언어 설정
    const stored = getStoredLanguage();
    if (stored) return stored;
    // 2순위: 브라우저 언어 감지
    return detectLanguage();
  });

  const setLang = (newLang: Language) => {
    setLangState(newLang);
    storeLanguage(newLang);
  };

  // 언어 변경 시 html lang 속성 업데이트
  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  const value: LanguageContextType = {
    lang,
    setLang,
    t: translations[lang],
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage(): LanguageContextType {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}

// 날짜 포맷팅 헬퍼
export function formatDate(date: Date, lang: Language): string {
  const locale = lang === 'ko' ? 'ko-KR' : 'en-US';
  return date.toLocaleString(locale, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

// UTC 시간 문자열을 로컬 시간 Date로 변환
// 백엔드가 UTC 시간을 시간대 정보 없이 반환하므로, 명시적으로 UTC로 파싱
export function parseUTCDate(dateString: string): Date {
  // 이미 Z가 있거나 시간대가 있으면 그대로 파싱
  if (dateString.endsWith('Z') || /[+-]\d{2}:\d{2}$/.test(dateString)) {
    return new Date(dateString);
  }
  // UTC 시간대 suffix 추가
  return new Date(dateString + 'Z');
}

